import { Routes, Route, useLocation } from 'react-router-dom'
import { useEffect, lazy, Suspense } from 'react'
import Layout from './components/Layout'
import Home from './pages/Home'

const Services = lazy(() => import('./pages/Services'))
const AboutUs = lazy(() => import('./pages/AboutUs'))
const Contact = lazy(() => import('./pages/Contact'))
const GetQuote = lazy(() => import('./pages/GetQuote'))
const ComplianceServices = lazy(() => import('./pages/ComplianceServices'))
const NotFound = lazy(() => import('./pages/NotFound'))
const FloridaRegulations = lazy(() => import('./pages/regulations/FloridaRegulations'))
const GeorgiaRegulations = lazy(() => import('./pages/regulations/GeorgiaRegulations'))
const SouthCarolinaRegulations = lazy(() => import('./pages/regulations/SouthCarolinaRegulations'))
const NorthCarolinaRegulations = lazy(() => import('./pages/regulations/NorthCarolinaRegulations'))
const FloridaGuide = lazy(() => import('./pages/regulations/FloridaGuide'))
const GeorgiaGuide = lazy(() => import('./pages/regulations/GeorgiaGuide'))
const SouthCarolinaGuide = lazy(() => import('./pages/regulations/SouthCarolinaGuide'))
const NorthCarolinaGuide = lazy(() => import('./pages/regulations/NorthCarolinaGuide'))

// SEO page titles and descriptions
const pageMeta: Record<string, { title: string; description: string }> = {
  '/': {
    title: 'Apex Diesel Storage Compliance | Fuel Storage Tank Inspection & Compliance',
    description: 'Professional fuel storage tank inspection and compliance services in Georgia, Florida, South Carolina, and North Carolina.'
  },
  '/services': {
    title: 'Our Services | Apex Diesel Storage Compliance',
    description: 'Comprehensive tank inspection services, monthly walkthroughs, hydrostatic testing, and compliance audits for fuel storage facilities.'
  },
  '/about': {
    title: 'About Us | Apex Diesel Storage Compliance',
    description: 'Learn about Apex Diesel Storage Compliance and our expert team of fuel storage tank inspectors serving GA, FL, SC, and NC.'
  },
  '/contact': {
    title: 'Contact Us | Apex Diesel Storage Compliance',
    description: 'Get in touch with Apex Diesel Storage Compliance for professional fuel tank inspection and compliance services.'
  },
  '/compliance-services': {
    title: 'Compliance Management Services | Apex Diesel Storage Compliance',
    description: 'Let Apex DSC handle all your diesel storage tank compliance requirements. Monthly and annual service contracts for inspections, testing, and regulatory management across GA, FL, SC, and NC.'
  },
  '/get-quote': {
    title: 'Request a Quote | Apex Diesel Storage Compliance',
    description: 'Request a custom quote for diesel storage tank inspections, compliance audits, hydrostatic testing, and monthly walkthroughs across GA, FL, SC, and NC.'
  },
  '/regulations/florida': {
    title: 'Florida Storage Tank Compliance Regulations | UST & AST Inspections | Apex DSC',
    description: 'Florida DEP storage tank compliance guide — UST and AST registration, release detection, spill bucket testing, annual operability testing, periodic integrity testing, and certified operator requirements.'
  },
  '/regulations/georgia': {
    title: 'Georgia UST Compliance Regulations | EPD Inspections | Apex DSC',
    description: 'Georgia EPD underground storage tank compliance guide — release detection monitoring, annual operability testing, cathodic protection, line leak detectors, spill bucket inspections, and what to expect during a compliance inspection.'
  },
  '/regulations/south-carolina': {
    title: 'South Carolina UST Compliance Regulations | SCUSTCR 61-92 Part 280 | Apex DSC',
    description: 'South Carolina DHEC underground storage tank compliance guide — walkthrough inspections, spill bucket testing, sump integrity testing, overfill prevention, release detection, and operator training under SCUSTCR 61-92 Part 280.'
  },
  '/regulations/north-carolina': {
    title: 'North Carolina UST Compliance Regulations | 15A NCAC 2N | Apex DSC',
    description: 'North Carolina DEQ underground storage tank compliance guide — cathodic protection testing, spill bucket integrity, containment sump testing, release detection, operator training, and required forms under 15A NCAC 2N.'
  },
  '/regulations/florida/guide': {
    title: 'Florida UST & AST Compliance Guide | DEP Inspections & IPTF | Apex DSC',
    description: 'Essential guide for Florida storage tank owners — common DEP violations, the Inland Protection Trust Fund, karst geology risks, hurricane preparedness, and practical inspection tips.'
  },
  '/regulations/georgia/guide': {
    title: 'Georgia UST Compliance Guide | EPD Inspections & GUST Trust Fund | Apex DSC',
    description: 'Essential guide for Georgia tank owners — common EPD violations, the GUST Trust Fund, Piedmont and Coastal Plain geology risks, and practical inspection preparation tips.'
  },
  '/regulations/south-carolina/guide': {
    title: 'South Carolina UST Compliance Guide | SCDES Inspections & SUPERB Fund | Apex DSC',
    description: 'Essential guide for South Carolina tank owners — common SCDES violations, the SUPERB Fund, Lowcountry water table risks, and practical inspection preparation tips.'
  },
  '/regulations/north-carolina/guide': {
    title: 'North Carolina UST Compliance Guide | DEQ Inspections & Cleanup Fund | Apex DSC',
    description: 'Essential guide for North Carolina tank owners — common DEQ violations, the Commercial Cleanup Trust Fund, Piedmont geology risks, and practical inspection preparation tips.'
  }
}

function App() {
  const location = useLocation()

  // Load Crisp chat widget
  useEffect(() => {
    (window as any).$crisp = [];
    (window as any).CRISP_WEBSITE_ID = '16538799-9017-427f-96a9-15da5bfdc813';
    const s = document.createElement('script')
    s.src = 'https://client.crisp.chat/l.js'
    s.async = true
    document.head.appendChild(s)
  }, [])

  useEffect(() => {
    // Scroll to top on route change
    window.scrollTo(0, 0)

    // Get page meta for current path or use default
    const currentPath = location.pathname
    const meta = pageMeta[currentPath] ||
      { title: 'Apex Diesel Storage Compliance', description: 'Professional tank inspection services' }

    // Update document title
    document.title = meta.title

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', meta.description)
    }

    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]')
    if (canonical) {
      canonical.setAttribute('href', `https://www.apexdsc.com${currentPath === '/' ? '' : currentPath}`)
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]')
    if (ogTitle) ogTitle.setAttribute('content', meta.title)
    const ogDescription = document.querySelector('meta[property="og:description"]')
    if (ogDescription) ogDescription.setAttribute('content', meta.description)
    const ogUrl = document.querySelector('meta[property="og:url"]')
    if (!ogUrl) {
      const newOgUrl = document.createElement('meta')
      newOgUrl.setAttribute('property', 'og:url')
      newOgUrl.setAttribute('content', `https://www.apexdsc.com${currentPath === '/' ? '' : currentPath}`)
      document.head.appendChild(newOgUrl)
    } else {
      ogUrl.setAttribute('content', `https://www.apexdsc.com${currentPath === '/' ? '' : currentPath}`)
    }

    // Update Twitter tags
    const twitterTitle = document.querySelector('meta[name="twitter:title"]')
    if (twitterTitle) twitterTitle.setAttribute('content', meta.title)
    const twitterDescription = document.querySelector('meta[name="twitter:description"]')
    if (twitterDescription) twitterDescription.setAttribute('content', meta.description)
  }, [location])

  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="services" element={<Services />} />
          <Route path="about" element={<AboutUs />} />
          <Route path="contact" element={<Contact />} />
          <Route path="compliance-services" element={<ComplianceServices />} />
          <Route path="get-quote" element={<GetQuote />} />
          <Route path="regulations/florida" element={<FloridaRegulations />} />
          <Route path="regulations/georgia" element={<GeorgiaRegulations />} />
          <Route path="regulations/south-carolina" element={<SouthCarolinaRegulations />} />
          <Route path="regulations/north-carolina" element={<NorthCarolinaRegulations />} />
          <Route path="regulations/florida/guide" element={<FloridaGuide />} />
          <Route path="regulations/georgia/guide" element={<GeorgiaGuide />} />
          <Route path="regulations/south-carolina/guide" element={<SouthCarolinaGuide />} />
          <Route path="regulations/north-carolina/guide" element={<NorthCarolinaGuide />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  )
}

export default App
